import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Popover,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import {useEffect, useMemo, useRef, useState} from 'react';
import {DateRange, RangeKeyDict} from 'react-date-range';

const SOFT_WARNING_DAYS = 90;

type Props = {
  labels?: [string, string];
  value?: [Date | null, Date | null];
  disabled?: boolean;
  size?: 'small' | 'medium';
  maxDays?: number;
  maxDate?: Date | null;
  onChange?: (value: [Date | null, Date | null]) => any;
};

export const DateRangeSelect = ({
  labels,
  value,
  disabled,
  size,
  maxDays,
  maxDate = dayjs().toDate(),
  onChange,
}: Props) => {
  // ─── committed range (mirrors the value prop) ────────────────────────────
  const initialStart = useMemo(
    () =>
      value?.[0]
        ? dayjs(value[0]).startOf('day').toDate()
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value?.[0]]
  );
  const initialEnd = useMemo(
    () =>
      value?.[1]
        ? dayjs(value[1]).startOf('day').toDate()
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value?.[1]]
  );

  const fallback = maxDate ?? dayjs().toDate();

  // ─── draft (working copy while the popover is open) ──────────────────────
  const [draft, setDraft] = useState<{startDate: Date; endDate: Date}>({
    startDate: initialStart ?? fallback,
    endDate: initialEnd ?? fallback,
  });

  // Sync draft when the committed value is changed from outside
  useEffect(() => {
    setDraft({
      startDate: initialStart ?? fallback,
      endDate: initialEnd ?? fallback,
    });
    // fallback intentionally omitted — it only needs to change with initialStart/End
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStart, initialEnd]);

  // ─── popover anchor ───────────────────────────────────────────────────────
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverOpen = Boolean(anchorEl);

  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }
    // Reset draft to committed each time the calendar opens
    setDraft({
      startDate: initialStart ?? fallback,
      endDate: initialEnd ?? fallback,
    });
    setAnchorEl(e.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  const handleCancel = () => {
    closePopover();
    // No onChange — committed range stays
  };

  // ─── large-range soft warning dialog ─────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmedKeyRef = useRef<string | null>(null);

  // ─── commit (shared by OK button and click-outside) ──────────────────────
  const fireOnChange = (start: Date, end: Date) => {
    const newValue: [Date | null, Date | null] = [start, end];
    if (!isEqual(value, newValue)) {
      onChange?.(newValue);
    }
  };

  const attemptCommit = () => {
    // If the confirm dialog is already open, the Popover onClose should not
    // double-trigger a commit — the dialog has its own Continue/Cancel path.
    if (confirmOpen) {
      return;
    }

    const {startDate, endDate} = draft;
    const days = dayjs(endDate).diff(dayjs(startDate), 'day');

    // Hard limit exceeded → discard draft and close (previous range stays)
    if (maxDays != null && days > maxDays) {
      setDraft({
        startDate: initialStart ?? fallback,
        endDate: initialEnd ?? fallback,
      });
      closePopover();
      return;
    }

    // Soft warning for large ranges (only fire once per unique range)
    const key = `${startDate.getTime()}:${endDate.getTime()}`;
    if (days > SOFT_WARNING_DAYS && confirmedKeyRef.current !== key) {
      setConfirmOpen(true);
      // Leave the popover open behind the dialog so the user can adjust
      return;
    }

    fireOnChange(startDate, endDate);
    closePopover();
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    // Leave popover open — user can keep adjusting the range
  };

  const handleConfirmContinue = () => {
    const {startDate, endDate} = draft;
    const key = `${startDate.getTime()}:${endDate.getTime()}`;
    confirmedKeyRef.current = key;
    setConfirmOpen(false);
    fireOnChange(startDate, endDate);
    closePopover();
  };

  // ─── collapsed display ────────────────────────────────────────────────────
  const fmt = (d: Date | null) =>
    d ? dayjs(d).format('YYYY-MM-DD') : '?';

  const triggerValue =
    initialStart || initialEnd
      ? `${fmt(initialStart)} → ${fmt(initialEnd)}`
      : '';

  return (
    <>
      <TextField
        label={labels?.[0] ?? 'Date range'}
        value={triggerValue}
        size={size}
        fullWidth
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthIcon
                fontSize="small"
                sx={{color: 'action.active', pointerEvents: 'none'}}
              />
            </InputAdornment>
          ),
        }}
        onClick={openPopover}
        sx={{cursor: disabled ? 'default' : 'pointer', '& input': {cursor: 'inherit'}}}
      />

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={attemptCommit}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableEnforceFocus
      >
        <Box display="flex" flexDirection="column">
          <DateRange
            months={2}
            direction="horizontal"
            moveRangeOnFirstSelection={false}
            showDateDisplay={false}
            ranges={[{...draft, key: 'selection'}]}
            maxDate={maxDate ?? dayjs().toDate()}
            onChange={(e: RangeKeyDict) => {
              const sel = e.selection;
              if (sel?.startDate && sel?.endDate) {
                setDraft({
                  startDate: dayjs(sel.startDate).startOf('day').toDate(),
                  endDate: dayjs(sel.endDate).startOf('day').toDate(),
                });
              }
            }}
          />

          <Box
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={attemptCommit}>
              OK
            </Button>
          </Box>
        </Box>
      </Popover>

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmCancel}
        disableEnforceFocus
      >
        <DialogTitle>Large date range</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dayjs(draft.endDate).diff(dayjs(draft.startDate), 'day')} days
            selected. Data may take a long time to load.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel}>Cancel</Button>
          <Button onClick={handleConfirmContinue}>Continue</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
